var Shopify = Shopify || {};
Shopify.money_format = '${{amount}}';
// ---------------------------------------------------------------------------
// Money format handler
// ---------------------------------------------------------------------------
Shopify.formatMoney = function (cents, format) {
  if (typeof cents == 'string') {
    cents = cents.replace('.', '');
  }
  let value = '';
  const placeholderRegex = /{{\s*(\w+)\s*}}/;
  const formatString = format || this.money_format;
  function defaultOption(opt, def) {
    return typeof opt == 'undefined' ? def : opt;
  }
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ',');
    decimal = defaultOption(decimal, '.');
    if (isNaN(number) || number == null) {
      return 0;
    }
    number = (number / 100.0).toFixed(precision);
    const parts = number.split('.'),
      dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
      cents = parts[1] ? decimal + parts[1] : '';
    return dollars + cents;
  }
  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
  }
  return formatString.replace(placeholderRegex, value);
};
/**
 * ----------------------------------------------------------------------
 * Helper functions list:
 * Cookies:
 * 1. setCookie - setting a browser cookie.
 * 2. getCookie - get cookie data by name.
 * 3. eraseCookie - remove cookie by name.
 *
 * Function calls:
 * 4. debounce - Used generally for scroll/input change/resize events to
 * avoid updating state too often.
 *
 * DOM Manipulation:
 * 5. bodyScroll - combining object used for body scroll locking.
 * 6. getFocusableElements - get focusable elements within a container.
 * 7. trapFocus - trap focus within a container.
 * 8. removeTrapFocus - remove the trap focus.
 * 9. pauseAllMedia - Pause all page media.
 * 10. getOffsetTop - Get offset from start of document.
 *
 * Data operations:
 * 11. serializeForm - turns formData into a JSON string. Accepts form el.
 * 12. decode - Decode a URI string.
 * 13. deepClone - get deep copy of an object.
 * 14. handleize - transform string to lowercase, replace spaces and low
 * dashes with a dash.
 * 15. fetchConfig - config object for a fetch request.
 * ----------------------------------------------------------------------
 */

/**
 * Debounce - group a series of sequential calls to a function
 * into a single call to that function after it stops getting
 * called for `wait` amount of ms.
 * @param {Function} fn
 * @param {Number} wait
 */
const debounce = (fn, wait) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
};

/**
 * Set cookie.
 * @param {String} name
 * @param {String} value
 * @param {Number} days
 * @returns Void
 */
const setCookie = (name, value, days) => {
  var expires = '';
  if (days) {
    var date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = '; expires=' + date.toUTCString();
  }
  document.cookie = name + '=' + (value || '') + expires + '; path=/';
};

/**
 * Get cookie data.
 * @param {String} name
 * @returns {String|Null}
 */
const getCookie = (name) => {
  var nameEQ = name + '=';
  var ca = document.cookie.split(';');
  for (var i = 0; i < ca.length; i++) {
    var c = ca[i];
    while (c.charAt(0) == ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

/**
 * Erase cookie value
 * @param {String} name
 */
const eraseCookie = (name) => {
  document.cookie = name + '=; Max-Age=-99999999';
};

/**
 * Body Scroll Lock global object.
 * Use this to ensure body scroll is locked across browsers and OS.
 * More info: https://github.com/willmcpo/body-scroll-lock#usage-examples
 */
const bodyScroll = {
  lock(container) {
    bodyScrollLock.disableBodyScroll(container);
  },
  unlock(container) {
    bodyScrollLock.enableBodyScroll(container);
  },
  clear() {
    bodyScrollLock.clearAllBodyScrollLocks();
  },
};

/**
 * Get all focusable elements within a given container.
 * @param {DOM Element} container
 * @returns
 */
const getFocusableElements = (container) => {
  const selectors = [
    'summary',
    'a[href]',
    'button:enabled',
    '[tabindex]:not([tabindex^="-"])',
    '[draggable]',
    'area',
    'input:not([type=hidden]):enabled',
    'select:enabled',
    'textarea:enabled',
    'object',
    'iframe',
  ];
  return Array.from(container.querySelectorAll(selectors.join(',')));
};

const trapFocusHandlers = {};

/**
 * Remove trap focus.
 * @param {DOM Element} elementToFocus
 */
const removeTrapFocus = (elementToFocus = null) => {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
};

/**
 * Trap focus within a container.
 * @param {DOM Element} container to trap focus within
 * @param {DOM Element} elementToFocus on when focus trapped
 */
const trapFocus = (container, elementToFocus = container) => {
  const elements = getFocusableElements(container);
  const first = elements[0];
  const last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();
};

/**
 * Adjust viewport-height css variable.
 */
['load', 'scroll', 'resize'].forEach((eventName) => {
  window.addEventListener(eventName, (e) => {
    document.documentElement.style.setProperty(
      '--viewport-height',
      `${window.innerHeight}px`,
    );
  });
});

/**
 * Close Details element on Esc key press.
 * @param {Event Object} event
 */
const onKeyUpEscape = (event) => {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
};

/**
 * Serialize form.
 * @param {DOM Element} form
 * @returns {String} JSON Object
 */
const serializeForm = (form) => {
  const obj = {};
  const formData = new FormData(form);
  for (const key of formData.keys()) {
    obj[key] = formData.get(key);
  }
  return JSON.stringify(obj);
};

/**
 * Deep cloning of Object.
 * @param {Object} obj
 * @returns {Object} clone
 */
const deepClone = (obj) => {
  return JSON.parse(JSON.stringify(obj));
};

/**
 * Handleize a string. Lowercase and replace spaces and low dashes
 * with a dash.
 * @param {String} str
 * @returns {String} Handleized string
 */
const handleize = (str) => str.replace(/[ /_]/g, '-').toLowerCase();

/**
 * Decode URI string
 * @param {String} str
 * @returns {String} Decoded string
 */
const decode = (str) => decodeURIComponent(str).replace(/\+/g, ' ');

/**
 * Get element offset from start of document.
 * @param {DOM Element} element
 * @returns {Number} Offset top
 */
const getOffsetTop = (element) => {
  let offsetTop = 0;

  do {
    if (!isNaN(element.offsetTop)) {
      offsetTop += element.offsetTop;
    }
  } while ((element = element.offsetParent));

  return offsetTop;
};

/**
 * Pause all media on page.
 */
function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + 'pauseVideo' + '","args":""}',
      '*',
    );
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

/**
 * Configuration object for a fetch request.
 * @param {String} type of content.
 * @returns {Object} The fetch configuration.
 */
const fetchConfig = (type = 'json') => {
  return {
    method: 'POST',
    headers: {
      'Content-Type': `application/${type}`,
      'Accept': `application/${type}`,
    },
  };
};

/**
 * Handle details aria attributes.
 */
document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute(
    'aria-expanded',
    summary.parentNode.hasAttribute('open'),
  );

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute(
      'aria-expanded',
      !event.currentTarget.closest('details').hasAttribute('open'),
    );
  });

  if (summary.closest('header-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

/**
 * Menu Drawer Custom element class.
 */
class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this)),
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const isOpen = detailsElement.hasAttribute('open');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling);
      summaryElement.nextElementSibling.removeEventListener(
        'transitionend',
        addTrapFocus,
      );
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(summaryElement)
        : this.openMenuDrawer(summaryElement);
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        summaryElement.nextElementSibling.addEventListener(
          'transitionend',
          addTrapFocus,
        );
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    bodyScroll.lock(summaryElement.nextElementSibling);
  }

  closeMenuDrawer(elementToFocus = false) {
    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle
      .querySelectorAll('.submenu-open')
      .forEach((submenu) => {
        submenu.classList.remove('submenu-open');
      });
    bodyScroll.unlock(
      this.mainDetailsToggle.querySelector('summary').nextElementSibling,
    );
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);
  }

  onFocusOut(event) {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute('open') &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement
      .querySelector('summary')
      .setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(
            detailsElement.closest('details[open]'),
            detailsElement.querySelector('summary'),
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

/**
 * Header drawer custom element class.
 */
class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    this.header =
      this.header ||
      document.querySelector(
        '#shopify-section-header header, .shopify-section-group-header-group header',
      );
    this.debouncedResize = debounce(() => {
      if (
        !window.matchMedia(
          `(min-width: ${this.getAttribute('data-breakpoint')}px)`,
        ).matches
      )
        return;

      this.closeMenuDrawer();
    }, 300);
    window.addEventListener('resize', this.debouncedResize.bind(this));
  }

  openMenuDrawer(summaryElement) {
    document.documentElement.style.setProperty(
      '--header-top-position',
      `${parseInt(this.header.offsetHeight)}px`,
    );
    this.header.classList.add('menu-open');
    super.openMenuDrawer(summaryElement);
  }

  closeMenuDrawer(elementToFocus) {
    super.closeMenuDrawer(elementToFocus);
    this.header.classList.remove('menu-open');
  }
}

customElements.define('header-drawer', HeaderDrawer);

/**
 * Quantity Input custom element class.
 */
class QuantityInput extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });

    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this)),
    );
  }

  onButtonClick(event) {
    event.preventDefault();

    const previousValue = this.input.value;

    event.target.name === 'increment'
      ? this.input.stepUp()
      : this.input.stepDown();

    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);
  }
}

customElements.define('quantity-input', QuantityInput);

/**
 * Modal opener custom element class.
 * Used in combination with ModalDialog for inserting a dialog
 * trigger in the document.
 * `data-modal` property connects the modal with the modal-opener.
 * Must contain a button element.
 */
class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;

    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));

      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

/**
 * Modal dialog custom element class.
 * Inserts a modal that is connected to a modal-opener.
 */
class ModalDialog extends HTMLElement {
  constructor() {
    super();

    this.dialogHolder = this.querySelector('[role="dialog"]');
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      'click',
      this.hide.bind(this, false),
    );
    this.addEventListener('keyup', (event) => {
      if (event.code?.toUpperCase() === 'ESCAPE') this.hide();
    });
    this.addEventListener('click', (event) => {
      if (event.target === this) this.hide();
    });
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    bodyScroll.lock(this.dialogHolder);
    this.setAttribute('open', '');
    trapFocus(this, this.dialogHolder);
    window.pauseAllMedia();
  }

  hide() {
    bodyScroll.unlock(this.dialogHolder);
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.dialogHolder);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

/**
 * Deferred media custom element class.
 * Must contain a button and a template element
 */
class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;

    if (this.dataset.play !== undefined) {
      this.loadContent();
    } else {
      poster.addEventListener('click', this.loadContent.bind(this));
    }
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(
        this.querySelector('template').content.firstElementChild.cloneNode(
          true,
        ),
      );

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(
        content.querySelector('.deferred-media__wrapper'),
      );
      if (focus) deferredElement.focus();
    }

    const buttonTogglePlay = this.querySelector('.js-toggle-play');
    if (buttonTogglePlay) {
      buttonTogglePlay.addEventListener('click', () => {
        const video = this.querySelector('video');
        video.paused ? video.play() : video.pause();
        video.paused
          ? this.classList.add('paused')
          : this.classList.remove('paused');
      });
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

/**
 * Cart class for base cart actions
 * More info: https://shopify.dev/docs/api/ajax/reference/cart
 */
class Cart {
  /**
   * Get global live regions to update on cart change.
   * @returns {Array} Live region sections objects
   */
  static getLiveRegions() {
    return [
      {
        id: '#cart-counter',
        section: 'cart-counter',
        selector: '#shopify-section-cart-counter',
      },
    ];
  }

  /**
   * Add items to cart.
   * @param {Object} body Form data
   * @returns {Promise} Resolves with the cart object
   */
  static add(body, useJs = false) {
    if (!body) {
      return undefined;
    }

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    return fetch(`${routes.cart_add_url}`, {
      ...config,
      ...{ body },
    });
  }

  /**
   * Update cart quantity, properties and selling plan.
   * @param {String} body JSON object
   * @returns {Promise} Resolves with the cart object
   * @example Body: '{"line":1,"quantity":3}'
   */
  static update(body) {
    if (!body) {
      return undefined;
    }

    return fetch(`${routes.cart_change_url}`, {
      ...fetchConfig(),
      ...{ body },
    });
  }

  /**
   * Clear all items from cart.
   * @returns {Promise} Promise
   */
  static clear() {
    return fetch(`${routes.cart_clear_url}`, {
      ...fetchConfig(),
    });
  }

  /**
   * Get cart Object.
   * @returns {Promise} Resolves with the cart object
   */
  static get() {
    return fetch(`${routes.cart_url}`, {
      ...fetchConfig(),
    });
  }
}

/**
 * Product selector.
 *
 * Requirements:
 * 1. Options must have `data-name` attribute.
 * 2. Product form with unique ID.
 * 3. Price container must have unique ID.
 * 3. Component Attributes:
 *    1. data-url
 *    2. data-form-id
 *    3. data-price-id
 * -------------------------------
 * 4. Required scripts:
    <script type="application/json" data-variants-json>
      {{- product.variants | json -}}
    </script>

    <script type="application/json" data-variants-prices>
      [
        {%- for variant in product.variants -%}
          {%- capture price_html -%}
            {% render 'price', product_ref: product, variant: variant, use_variant: true %}
          {%- endcapture -%}
          {
            "id": {{ variant.id | json }},
            "price_html": {{ price_html | json }},
            "price_single": {{ variant.price | money | json }}
          }{%- unless forloop.last -%},{%- endunless -%}
        {%- endfor -%}
      ]
    </script>
 */

class ProductSelector extends HTMLElement {
  constructor() {
    super();
  }

  static currenciesWithoutDecimals = [
    'BIF', // Burundian Franc
    'CLP', // Chilean Peso
    'DJF', // Djiboutian Franc
    'GNF', // Guinean Franc
    'ISK', // Icelandic Króna
    'JPY', // Japanese Yen
    'KMF', // Comorian Franc
    'KRW', // South Korean Won
    'PYG', // Paraguayan Guarani
    'RWF', // Rwandan Franc
    'UGX', // Ugandan Shilling
    'VND', // Vietnamese Đồng
    'VUV', // Vanuatu Vatu
    'XAF', // Central African CFA Franc
    'XOF', // West African CFA Franc
    'XPF', // CFP Franc
  ];

  /**
   * Connected callback.
   */
  connectedCallback() {
    this.form = document.querySelector(
      `form#${this.getAttribute('data-form-id')}`,
    );
    this.form?.addEventListener('submit', this.onSubmitHandler.bind(this));
    this.submitButton = this.form?.elements['add'];
    this.cartDrawer = document.querySelector('cart-drawer');
    this.variants = JSON.parse(
      this.querySelector('[data-variants-json][type="application/json"]')
        .textContent,
    );
    this.prices = JSON.parse(
      this.querySelector('[data-variants-prices]').textContent,
    );
    this.inventory_policy = this.querySelector(
      '[data-variants-inventory-policy]',
    );
    this.variantInventoryPolicy = this.inventory_policy
      ? JSON.parse(this.inventory_policy.textContent)
      : [];
    this.addEventListener('change', this.onVariantChange.bind(this));
    this.unavailableText = ` - ${window.variantStrings.unavailable}`;
    this.priceContainer = document.querySelector(
      `#${this.getAttribute('data-price-id')}`,
    );

    this.quantityInput = this.querySelector('[name="quantity"]');
    if (this.quantityInput) {
      this.quantityInput.addEventListener(
        'change',
        this.onQuantityChange.bind(this),
      );
    }

    this.addEventListener(
      'wholesale:quantity-change',
      this.handleWholesaleQuantityChange.bind(this),
    );

    this.generateWholesaleBreakpoints();

    this.setInitialVariant();

    this.updateOptions();
    this.filterOptions();
    this.updateBackordered();

    this.querySelectorAll('[js-form-click]').forEach((element) =>
      element.click(),
    );

    this.addEventListener(
      'selector:update-price',
      this.handleUpdatePriceWB.bind(this),
    );
  }

  handleUpdatePriceWB(event) {
    setTimeout(() => {
      const quantity = this.quantityInput.value;
      let currentActiveButton = null;

      this.querySelectorAll('[js-active]').forEach((button) => {
        if (quantity < Number(button.getAttribute('data-min-quantity'))) return;

        currentActiveButton = button;
      });

      if (!currentActiveButton) return;

      const priceHtml = this.formatCurrency(
        Number(currentActiveButton.getAttribute('data-price-per-item')),
      );

      this.updatePriceManual(priceHtml);
    }, 1);
  }

  updatePriceManual(price) {
    setTimeout(() => {
      this.priceContainer.innerHTML = price;
    }, 1);
  }

  onQuantityChange() {
    document.dispatchEvent(
      new CustomEvent('wholesale:update', {
        detail: {
          quantity: this.quantityInput.value,
          productId: this.getAttribute('data-product-id'),
        },
      }),
    );

    let currentActiveButton = null;
    const quantity = this.quantityInput.value;

    this.querySelectorAll('[js-active]').forEach((button) => {
      if (quantity < Number(button.getAttribute('data-min-quantity'))) return;

      currentActiveButton = button;
    });

    if (!currentActiveButton) return;

    const priceHtml = this.formatCurrency(
      Number(currentActiveButton.getAttribute('data-price-per-item')),
    );

    this.updatePriceManual(priceHtml);
  }

  /**
   * Creates an array of Objects from the 'wholesale-button' elements, filtering
   * the inactive [data-type="variant"] buttons.
   */
  generateWholesaleBreakpoints() {
    this.wholesaleBreakpoints = Array.from(
      this.querySelectorAll('wholesale-button'),
    )
      .filter(
        (element) =>
          !element.hasAttribute('data-type') ||
          (element.hasAttribute('data-type') &&
            element.hasAttribute('js-active')),
      )
      .map((element) => {
        return {
          minQuantity: Number(element.getAttribute('data-min-quantity')),
          discount: Number(element.getAttribute('data-percent-discount')),
          price: Number(element.getAttribute('data-price')),
          variantId: Number(element.getAttribute('data-variant-id')),
        };
      })
      .sort((a, b) => a.minQuantity - b.minQuantity);
  }

  handleWholesaleQuantityChange(event) {
    let newQuantity = event.detail.quantity;

    if (this.quantityInput.value > 1) {
      newQuantity += Number(this.quantityInput.value);
    }

    this.quantityInput.value = newQuantity;

    this.updatePrice();
  }

  setInitialVariant() {
    if (this.variants.length == 1) {
      this.currentVariant = this.variants[0];

      return this.updatePrice();
    }

    const initialVariantId = this.getAttribute('data-initial-variant-id');
    if (!initialVariantId) return;

    this.currentVariant = this.variants.find(
      (variant) => variant.id == initialVariantId,
    );

    this.updatePrice();
  }

  handlePreorder() {
    const preorderToggle = this.querySelector('[js-preorder-toggle]');
    preorderToggle.setAttribute('data-variant-id', this.currentVariant.id);
    preorderToggle.click();
  }

  /**
   * On submit handler.
   *
   * @param {Object} event
   * @returns {Void}
   */
  onSubmitHandler(event) {
    event.preventDefault();

    if (this.submitButton.hasAttribute('js-preorder'))
      return this.handlePreorder();

    this.submitButton.classList.add('disabled');

    const formData = new FormData(this.form);
    // formData.append(
    //   'sections',
    //   this.cartDrawer.getSectionsToRender().map((section) => section.section),
    // );
    formData.append('sections', 'cart-counter');
    formData.append('sections_url', window.location.pathname);

    Cart.add(formData)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.handleErrorMessage(response.description);
          return;
        }

        if (response.sections['cart-counter']) {
          const counterHTML = new DOMParser()
            .parseFromString(response.sections['cart-counter'], 'text/html')
            .querySelector('#shopify-section-cart-counter').innerHTML;
          document.querySelector('#cart-counter').innerHTML = counterHTML;
        }

        if (!this.currentVariantPrice) {
          this.currentVariantPrice = this.prices.find((priceObj) => {
            return priceObj.id === this.currentVariant?.id;
          });
        }

        this.currentSubmitButtonInnerHTML = `${this.submitButton.innerHTML}`;

        this.submitButton.innerHTML = `<svg class="success" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M15 6H17V8H15V6ZM13 10V8H15V10H13ZM11 12V10H13V12H11ZM9 14V12H11V14H9ZM7 16V14H9V16H7ZM5 16H7V18H5V16ZM3 14H5V16H3V14ZM3 14H1V12H3V14ZM11 16H13V18H11V16ZM15 14V16H13V14H15ZM17 12V14H15V12H17ZM19 10V12H17V10H19ZM21 8H19V10H21V8ZM21 8H23V6H21V8Z" fill="white"/></svg>`;
        this.classList.add('is-adding');

        setTimeout(() => {
          this.classList.remove('is-adding');
          this.submitButton.innerHTML = this.currentSubmitButtonInnerHTML;
        }, 2500);

        // document.dispatchEvent(
        //   new CustomEvent('cart:added', {
        //     detail: {
        //       title: this.currentVariantPrice.title,
        //       image: this.currentVariantPrice.image,
        //     },
        //   }),
        // );
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.submitButton.classList.remove('disabled');
      });
  }

  /**
   * Handle error message.
   *
   * @param {String/Object} errorMessage Comes from Shopify.
   * @returns {Void}
   */
  handleErrorMessage(errorMessage = false) {
    const errorWrapper = this.querySelector('[data-error-wrapper]');
    if (!errorWrapper || !errorMessage) return;

    window.dispatchEvent(
      new CustomEvent('formError', { detail: errorMessage }),
    );
    errorWrapper.classList.toggle('hidden', !errorMessage);

    if (typeof errorMessage == 'string') {
      errorWrapper.textContent = errorMessage || '';
    }
  }

  handleSuccessMessage(message = false) {
    const successWrapper = this.querySelector('[data-success-wrapper]');
    if (!successWrapper || !message) return;

    if (typeof message == 'string') {
      successWrapper.textContent = message || '';
      successWrapper.classList.remove('hidden');

      setTimeout(() => {
        successWrapper.classList.add('hidden');
      }, 10000);
    }
  }

  changeImagesAndSku() {
    const target = document.querySelector(
      `[js-image][data-variant-id="${this.currentVariant.id}"]`,
    );

    if (!target) return;

    const cardProduct = this.closest('.card-product');

    cardProduct.querySelectorAll('[js-image]').forEach((image) => {
      image.classList.add('hidden');
    });

    target.classList.remove('hidden');

    cardProduct.querySelectorAll('[js-sku-target]').forEach((element) => {
      element.textContent = target.getAttribute('data-variant-sku');
    });
  }

  updateWishlistComponent() {
    const wishlistComponent = this.parentElement.querySelector(
      'wishlist-modal-toggle',
    );
    if (!wishlistComponent) {
      console.error('Wishlist component not found');
      return;
    }

    const currentWishlistVariant = Number(
      wishlistComponent.getAttribute('data-variant-id'),
    );
    if (currentWishlistVariant === this.currentVariant.id) {
      return;
    }

    wishlistComponent.variantId = this.currentVariant.id.toString();
    wishlistComponent.setAttribute('data-variant-id', this.currentVariant.id);
  }

  /**
   * On variant change.
   *
   * @param {Object} event
   * @returns {Void}
   */
  onVariantChange(event) {
    if (event.target.type === 'number') return this.updatePrice();
    this.updateOptions();
    this.updateVariant();
    this.toggleAddButton(false, '');
    this.handleErrorMessage();
    this.filterOptions();

    this.changeImagesAndSku();
    this.updateWishlistComponent();

    if (!this.currentVariant) {
      this.toggleAddButton(true, '');
      this.setUnavailable();

      return;
    }

    if (!this.currentVariant.available) {
      this.toggleAddButton(true, window.variantStrings.soldOut);
    }

    this.updateVariantInput();

    this.querySelectorAll('wholesale-button').forEach((button) => {
      if (!button.hasAttribute('data-type')) return;

      button.toggleAttribute(
        'js-active',
        this.currentVariant?.id == button.getAttribute('data-variant-id'),
      );
    });

    this.generateWholesaleBreakpoints();
    this.updatePrice();
    this.updateBackordered();
  }

  updateBackordered() {
    this.backorderedElements = this.closest('.card-product')?.querySelectorAll(
      '[data-oos-variants]',
    );

    if (!this.backorderedElements) {
      this.backorderedElements = this.closest('.product')?.querySelectorAll(
        '[data-oos-variants]',
      );
    }

    if (!this.backorderedElements) return;

    this.backorderedElements.forEach((element) =>
      element.classList.toggle(
        'backordered-hidden',
        !element
          .getAttribute('data-oos-variants')
          .includes(this.currentVariant.id),
      ),
    );
  }

  /**
   * Update options.
   *
   * @returns {Void}
   */
  updateOptions() {
    this.options = Array.from(
      this.querySelectorAll('input[type="radio"]:checked, select'),
      (el) => ({ name: el.dataset.name, value: el.value }),
    );
  }

  /**
   * Update variant.
   *
   * @returns {Void}
   */
  updateVariant() {
    this.currentVariant = this.variants.find((variant) => {
      return !variant.options
        .map((option, index) => this.options[index]?.value === option)
        .includes(false);
    });

    this.currentVariantPrice = this.prices.find((priceObj) => {
      return priceObj.id === this.currentVariant?.id;
    });

    this.currentVariantInventoryPolicy = this.variantInventoryPolicy.find(
      (variant) => {
        return variant.id === this.currentVariant?.id;
      },
    );
  }

  /**
   * Update variant input.
   *
   * @returns {Void}
   */
  updateVariantInput() {
    const input = this.form?.elements['id'];

    if (!input) {
      return;
    }

    input.value = this.currentVariant?.id;
  }

  /**
   * Set unavailable.
   *
   * @returns {Void}
   */
  setUnavailable() {
    if (!this.submitButton) return;
    this.submitButton.textContent = window.variantStrings.unavailable;
    this.priceContainer?.classList.add('visually-hidden');
  }

  /**
   * Render product info.
   *
   * @returns {Void}
   */
  updatePrice() {
    if (this.currentVariant) {
      this.setAttribute('data-price', this.currentVariant.price);
      return this.calculatePrice(this.currentVariant.price);
    }

    if (!this.priceContainer || !this.currentVariantPrice?.price_html) {
      return;
    }

    this.priceContainer.classList.remove('visually-hidden');
    this.priceContainer.innerHTML = this.currentVariantPrice.price_html;
  }

  /**
   * Toggle add button.
   *
   * @param {Boolean} disable
   * @param {String} text
   * @returns {Void}
   */
  toggleAddButton(disable, text) {
    if (!this.submitButton) {
      return;
    }

    if (disable) {
      this.submitButton.setAttribute('disabled', 'disabled');
      if (text) this.submitButton.textContent = text;

      return;
    }

    this.submitButton.removeAttribute('disabled');
  }

  /**
   * Filter unavailable options.
   * Toggles 'is-unavailable' attribute based on options availability.
   *
   * @returns {Void}
   */
  filterOptions() {
    for (const [key, { name }] of this.options.entries()) {
      this.querySelectorAll(`[data-name="${name}"]`).forEach((swatch) => {
        const selectOptions = swatch.querySelectorAll('option');
        if (selectOptions.length) {
          return selectOptions.forEach((option) => {
            if (this.isOptionAvailable(key, option.value)) {
              return (option.innerHTML = option.innerHTML.replace(
                this.unavailableText,
                '',
              ));
            }

            if (option.innerHTML.includes(this.unavailableText)) return;

            option.innerHTML = `${option.innerHTML}${this.unavailableText}`;
          });
        }

        return swatch.classList.toggle(
          'is-unavailable',
          !this.isOptionAvailable(key, swatch.value),
        );
      });
    }
  }

  /**
   * @param {Number} value - Price. See {@link formatCurrency} amount parameter for details
   */
  calculatePrice(value) {
    const quantity = Number(this.quantityInput.value);
    let price = value;
    let activeBreakpoints = [];

    // Collect all of the active breakpoints in an array
    for (let i = 0; i < this.wholesaleBreakpoints.length; i++) {
      if (quantity < this.wholesaleBreakpoints[i].minQuantity) break;

      activeBreakpoints.push(this.wholesaleBreakpoints[i]);
    }

    // Calculate price for all active breakpoints
    activeBreakpoints.forEach((breakpoint) => {
      if (breakpoint.price) return;

      breakpoint.price = price - (price * breakpoint.discount) / 100;
    });

    // Sorting all active breakpoints by price to get the lowest price
    activeBreakpoints.sort((a, b) => a.price - b.price);

    if (activeBreakpoints[0]) {
      price = activeBreakpoints[0].price;
    }

    // You can multiply the price by the quantity here
    this.priceContainer.classList.remove('visually-hidden');
    this.priceContainer.innerHTML = this.formatCurrency(price);
  }

  /**
   * Formats a number as currency.
   *
   * @param {Number} amount - The amount to format. Using the same format
   * as shopify's filters: 9000 is 90.00 for decimal currencies.
   * @returns {String} The formatted currency string.
   */
  formatCurrency(amount) {
    const divisor = ProductSelector.currenciesWithoutDecimals.includes(
      window.currency,
    )
      ? 1
      : 100;

    return new Intl.NumberFormat(window.shopLocale, {
      style: 'currency',
      currency: window.currency,
    }).format(amount / divisor);
  }

  /**
   * Is option available.
   * Check if at least one available variant that contains this option exists.
   *
   * @param {Number} index
   * @param {String} value
   * @returns {Boolean}
   */
  isOptionAvailable(index, value) {
    return this.variants.some((variant) => {
      if (!variant.available) return false;

      const valuesToCheck = {};
      const currentHandle = `option${index + 1}`;

      for (let i = 0; i < index; i++) {
        const optionHandle = `option${i + 1}`;
        const optionValue = this.options[i].value;
        valuesToCheck[optionHandle] = optionValue;
      }

      valuesToCheck[currentHandle] = value;

      return Object.entries(valuesToCheck).every(
        ([handle, value]) => variant[handle] === value,
      );
    });
  }
}

customElements.define('product-selector', ProductSelector);

(() => {
  function setHeaderHeightProperty(header, announcementBar) {
    document.documentElement.style.setProperty(
      '--header-height',
      header.getBoundingClientRect().height + 'px',
    );

    document.documentElement.style.setProperty(
      '--scrollbar-width',
      Math.max(window.innerWidth - document.documentElement.clientWidth, 0) +
        'px',
    );

    if (!announcementBar) return;

    document.documentElement.style.setProperty(
      '--announcement-bar-height',
      announcementBar.getBoundingClientRect().height + 'px',
    );
  }

  const header = document.querySelector('header');
  const announcementBar = document.querySelector('.announcement-bar');
  setHeaderHeightProperty(header, announcementBar);

  window.addEventListener(
    'resize',
    () => {
      setHeaderHeightProperty(header, announcementBar);
    },
    { passive: true },
  );
})();

class ReturnTo extends HTMLElement {
  constructor() {
    super();
  }

  handleClick(event) {
    setCookie('returnUrl', window.location.href, 1);
  }

  connectedCallback() {
    this.addEventListener('click', this.handleClick.bind(this));
  }
}

customElements.define('return-to', ReturnTo);

function deleteCookie(name) {
  document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
}

(() => {
  const href = window.location.href;
  const returnUrl = getCookie('returnUrl');
  const loginForm = document.querySelector('#customer_login');

  if (getCookie('wasRedirected')) {
    eraseCookie('wasRedirected');
    eraseCookie('returnUrl');
    return;
  }

  if (!returnUrl) return;

  if (loginForm) {
    loginForm.setAttribute(
      'action',
      loginForm.getAttribute('action') + '?return_to=' + returnUrl,
    );

    loginForm.addEventListener('submit', () => {
      setCookie('wasRedirected', 'true', 1);
      eraseCookie('returnUrl');
    });
  }

  if (
    !window.hasCustomer ||
    (window.hasCustomer && window.is_wcp_wholesale_customer)
  )
    return;
  if (returnUrl == href) return;

  setCookie('wasRedirected', 'true', 1);
  eraseCookie('returnUrl');

  requestAnimationFrame(() => {
    window.location.href = returnUrl;
  });
})();

/**
 * Deep equal.
 *
 * @param {Object} obj1
 * @param {Object} obj2
 * @returns {Boolean}
 */
function deepEqual(obj1, obj2) {
  // Check if both values are strictly equal
  if (obj1 === obj2) return true;

  // Check if either value is null or not an object
  if (
    obj1 === null ||
    typeof obj1 !== 'object' ||
    obj2 === null ||
    typeof obj2 !== 'object'
  ) {
    return false;
  }

  // Handle arrays
  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) return false;
    // Sort the arrays before comparing
    const sorted1 = [...obj1].sort();
    const sorted2 = [...obj2].sort();
    return sorted1.every((item, index) => deepEqual(item, sorted2[index]));
  }

  // If one is an array and the other is not
  if (Array.isArray(obj1) || Array.isArray(obj2)) {
    return false;
  }

  // Get the keys of both objects
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  // Check if the number of keys is different
  if (keys1.length !== keys2.length) return false;

  // Check if every key and value in obj1 exists and is equal in obj2
  for (const key of keys1) {
    if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
}
